import React, { useState } from "react";

import { faExpand } from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, m } from "motion/react";
import { createPortal } from "react-dom";

import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { FloatingWindowInternal } from "src/base-components/FloatingWindow/FloatingWindow";
import { IconButton } from "src/design-system/Icon";

type ExpandableCodeEditorProps = {
  dataLoc: string;
  language: "python" | "json";
  placeholder: string;
  value: string;
  title: string;
  expandedWidth: `w-${number}`;
  expandedHeight: `h-${number}`;
  height?: `h-${number}`;
  disabled?: boolean;
  onChange: (value: string | undefined) => void;
  onBlur?: () => void;
};

export const ExpandableCodeEditor: React.FC<ExpandableCodeEditorProps> = ({
  dataLoc,
  language,
  placeholder,
  value,
  title,
  expandedWidth,
  expandedHeight,
  height,
  disabled = false,
  onChange,
  onBlur,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleBackdropClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.stopPropagation();
    setExpanded(false);
  };

  return (
    <div className="relative">
      {!disabled && (
        <div className="absolute top-1 right-1 z-10">
          <IconButton
            aria-label={`Expand ${title}`}
            icon={faExpand}
            size="2xs"
            onClick={() => setExpanded(true)}
          />
        </div>
      )}
      <div className={height}>
        <CodeEditor
          dataLoc={dataLoc}
          disabled={disabled}
          highlightOnFocus
          language={language}
          placeholder={placeholder}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
        />
      </div>
      {createPortal(
        <AnimatePresence>
          {expanded && (
            <m.div
              key="expanded-code-editor"
              animate={{ opacity: 1 }}
              className="fixed inset-0 z-50"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeInOut" }}
            >
              <button
                aria-label="Close expanded code editor"
                className="absolute inset-0 bg-black/50"
                data-loc={`${dataLoc}-expanded-backdrop`}
                type="button"
                onClick={handleBackdropClick}
              />
              <FloatingWindowInternal
                draggable={false}
                height={expandedHeight}
                isPinned={false}
                maximizable={true}
                style={{
                  inset: "0px auto auto 0px",
                  transform:
                    "translate3d(calc(50vw - 50%), calc(50vh - 50%), 0)",
                }}
                title={title}
                width={expandedWidth}
                onClose={() => setExpanded(false)}
              >
                <div className="h-full">
                  <CodeEditor
                    backgroundColor="white"
                    basicSetup={{
                      lineNumbers: true,
                      highlightActiveLine: true,
                    }}
                    dataLoc={`${dataLoc}-expanded`}
                    disabled={disabled}
                    language={language}
                    placeholder={placeholder}
                    value={value}
                    highlightOnFocus
                    lineWrapping
                    onBlur={onBlur}
                    onChange={onChange}
                  />
                </div>
              </FloatingWindowInternal>
            </m.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
};
